import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { usePreference } from './PreferencesContext';
import { nerdlet, NerdletStateContext } from 'nr1';

const MarketContext = createContext(null);

// Market configuration with real account IDs
export const MARKET_CONFIG = {
  UK: {
    accountId: 3547573,
    code: 'UK',
    name: 'United Kingdom',
    region: 'EMEA',
    timezone: 'Europe/London',
    currency: 'GBP',
  },
  PT: {
    accountId: 4091918,
    code: 'PT',
    name: 'Portugal',
    region: 'EMEA',
    timezone: 'Europe/Lisbon',
    currency: 'EUR',
  },
  CH: {
    accountId: 3592556,
    code: 'CH',
    name: 'Switzerland',
    region: 'EMEA',
    timezone: 'Europe/Zurich',
    currency: 'CHF',
  },
  DE: {
    accountId: 3497361,
    code: 'DE',
    name: 'Germany',
    region: 'EMEA',
    timezone: 'Europe/Berlin',
    currency: 'EUR',
  },
};

export const MarketProvider = ({ children }) => {
  // Restore the view from URL state so a reload or a shared link lands on the
  // same market. Inside New Relic the platform populates this context from the
  // nerdlet URL state written by nerdlet.setUrlState below; standalone, the
  // compat layer populates it from the query string. Same code, both targets.
  const urlState = useContext(NerdletStateContext) || {};

  /**
   * The view this reader was last on, remembered across sessions.
   *
   * Only ever consulted when the URL says nothing. An address with a market
   * in it is an explicit request — a bookmark, a shared link, a Back — and
   * must not be overridden by where this browser happened to be yesterday.
   */
  const [lastRoute, setLastRoute] = usePreference('lastRoute', null);
  const restoredMarket = MARKET_CONFIG[urlState.market] ? urlState.market : null;

  // useState only reads these on first render, which is exactly the restore.
  const [selectedMarket, setSelectedMarket] = useState(restoredMarket);
  const [viewMode, setViewMode] = useState(
    restoredMarket && urlState.view === 'market' ? 'market' : 'global'
  ); // 'global' | 'market'
  // The operational-health drill-down within a market. This was local
  // component state, so entering it left the URL on the market dashboard:
  // nothing was pushed, and Back skipped the market and went to global.
  const [panel, setPanel] = useState(
    restoredMarket && urlState.view === 'market' && urlState.panel === 'operational'
      ? 'operational'
      : null
  ); // null | 'operational'
  const [dateRange, setDateRange] = useState({
    duration: 1800000, // 30 minutes
    begin_time: null,
    end_time: null,
  });

  // Follow the URL after mount too. useState above only restores on first
  // render, so without this a Back or Forward changed the address bar and left
  // the view where it was.
  useEffect(() => {
    const market = MARKET_CONFIG[urlState.market] ? urlState.market : null;
    const view = market && urlState.view === 'market' ? 'market' : 'global';
    setSelectedMarket((prev) => (prev === market ? prev : market));
    setViewMode((prev) => (prev === view ? prev : view));
    const nextPanel =
      view === 'market' && urlState.panel === 'operational' ? 'operational' : null;
    setPanel((prev) => (prev === nextPanel ? prev : nextPanel));
  }, [urlState.market, urlState.view, urlState.panel]);

  // Restore on first render only, and only into an empty address bar.
  // Replace rather than push: the reader did not navigate here, so putting
  // the URL they never saw behind them would make Back look broken.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;

    if (urlState.market || urlState.view) return;
    const market = lastRoute && MARKET_CONFIG[lastRoute.market] ? lastRoute.market : null;
    if (!market) return;

    const panel = lastRoute.panel === 'operational' ? 'operational' : null;
    console.log(`[MarketContext] Restoring last view: ${market}${panel ? ' / ' + panel : ''}`);
    setSelectedMarket(market);
    setViewMode('market');
    setPanel(panel);
    try {
      nerdlet.setUrlState({ market, view: 'market', panel }, { replace: true });
    } catch (error) {
      console.warn('[MarketContext] URL state update failed:', error);
    }
    // Deliberately not reacting to later lastRoute changes — this is a
    // restore, not a subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remember where the reader ends up, including Back and Forward, so the
  // next session opens where this one left off rather than where it started.
  useEffect(() => {
    setLastRoute(
      viewMode === 'market' && selectedMarket
        ? { market: selectedMarket, panel }
        : { market: null, panel: null }
    );
  }, [viewMode, selectedMarket, panel, setLastRoute]);

  // Computed values
  const currentMarketConfig = useMemo(() => {
    return selectedMarket ? MARKET_CONFIG[selectedMarket] : null;
  }, [selectedMarket]);

  const accountId = useMemo(() => {
    return currentMarketConfig?.accountId || null;
  }, [currentMarketConfig]);

  // Navigation
  const navigateToMarket = useCallback((marketCode) => {
    console.log(`[MarketContext] Navigating to market: ${marketCode}`);
    setSelectedMarket(marketCode);
    setViewMode('market');
    setPanel(null);
    
    //  Use nerdlet.setUrlState
    try {
      nerdlet.setUrlState({
        market: marketCode,
        view: 'market',
        panel: null,
      });
    } catch (error) {
      console.warn('[MarketContext] URL state update failed:', error);
    }
    // Update New Relic URL state
    // navigation.replaceNerdletState({
    //   market: marketCode,
    //   view: 'market',
    // });
  }, []);

  const navigateToGlobal = useCallback(() => {
    console.log('[MarketContext] Navigating to global view');
    setSelectedMarket(null);
    setViewMode('global');
    setPanel(null);
     try {
      nerdlet.setUrlState({
        market: null,
        view: 'global',
        panel: null,
      });
    } catch (error) {
      console.warn('[MarketContext] URL state update failed:', error);
    }
    // navigation.replaceNerdletState({
    //   view: 'global',
    // });
  }, []);

  const navigateToOperational = useCallback(() => {
    console.log('[MarketContext] Navigating to operational health');
    setPanel('operational');
    try {
      nerdlet.setUrlState({ panel: 'operational' });
    } catch (error) {
      console.warn('[MarketContext] URL state update failed:', error);
    }
  }, []);

  const navigateToMarketDashboard = useCallback(() => {
    console.log('[MarketContext] Navigating back to the market dashboard');
    setPanel(null);
    try {
      nerdlet.setUrlState({ panel: null });
    } catch (error) {
      console.warn('[MarketContext] URL state update failed:', error);
    }
  }, []);

  const value = useMemo(() => ({
    // State
    selectedMarket,
    viewMode,
    panel,
    accountId,
    currentMarketConfig,
    dateRange,
    
    // Configuration
    allMarkets: Object.keys(MARKET_CONFIG),
    marketConfig: MARKET_CONFIG,
    
    // Actions
    navigateToMarket,
    navigateToGlobal,
    navigateToOperational,
    navigateToMarketDashboard,
    setDateRange,
    
    // Helpers
    isGlobalView: viewMode === 'global',
    isMarketView: viewMode === 'market',
    isOperationalView: viewMode === 'market' && panel === 'operational',
  }), [
    selectedMarket,
    viewMode,
    panel,
    accountId,
    currentMarketConfig,
    dateRange,
    navigateToMarket,
    navigateToGlobal,
    navigateToOperational,
    navigateToMarketDashboard,
  ]);

  return (
    <MarketContext.Provider value={value}>
      {children}
    </MarketContext.Provider>
  );
};

export const useMarket = () => {
  const context = useContext(MarketContext);
  if (!context) {
    throw new Error('useMarket must be used within MarketProvider');
  }
  return context;
};

export default MarketContext;